import type { SandboxProfile, SandboxSuggestion, SandboxVisibilityPoint } from './types'

/** How many open Suggestions the homepage's Plan panel and the mini-dashboard's "Up next" show. */
const SHOWN_ROWS = 3

/** A sample Suggestion with its reach counted from the sample, and its reason's `{n}` filled in. */
type SamplePlanSuggestion = SandboxSuggestion & {
  /** The sample prompts it reaches; absent when the Suggestion names none (a site-wide change). */
  buyerQuestions?: number
}

export interface SamplePlanSummary {
  /** The sample's headline visibility: the latest point of `profile.visibility`. */
  visibility: number
  /** The same latest point, per engine — the brand's row on the Competitors page. */
  perEngine: SandboxVisibilityPoint['perEngine']
  /** Latest point minus the one before it (points are weekly); 0 with fewer than two points. */
  visibilityDelta: number
  /**
   * How many open Suggestions there are: those not yet started as a Task. The one count behind the
   * "Suggestions" KPI, the Plan panel's pill and every "Showing N of M" caption — a caller
   * discloses the cut when it exceeds `upNext.length`.
   */
  suggestionCount: number
  tasksLive: number
  /** The open Suggestions, widest reach first, cut at `SHOWN_ROWS`: the Plan panel and "Up next". */
  upNext: SamplePlanSuggestion[]
  /** Suggestions started as a Task. */
  inProgress: SamplePlanSuggestion[]
}

/**
 * The sample prompts a Suggestion reaches, counted from the sample itself. A source-targeted one
 * reaches the prompts the Sources page lists for that source; a site-targeted one lists the prompt
 * ids it concerns. Ids that name no sample prompt are not counted.
 */
function reachedPrompts(
  profile: SandboxProfile,
  suggestion: SandboxSuggestion
): number | undefined {
  const known = new Set(profile.prompts.map((p) => p.id))
  const ids =
    suggestion.sourceDomain !== undefined
      ? profile.sources.find((s) => s.domain === suggestion.sourceDomain)?.promptsCitingIds
      : suggestion.promptIds
  if (ids === undefined) return undefined
  return new Set(ids.filter((id) => known.has(id))).size
}

function withReach(profile: SandboxProfile, suggestion: SandboxSuggestion): SamplePlanSuggestion {
  const buyerQuestions = reachedPrompts(profile, suggestion)
  return {
    ...suggestion,
    buyerQuestions,
    reason: suggestion.reason.replace('{n}', String(buyerQuestions ?? 0)),
  }
}

/**
 * The one source for every number the homepage's sample Growth Plan prints: the Plan tab, the
 * Track tab's visibility line and the mini-dashboard's Plan page all read this, so the sample
 * says one thing everywhere (R6.4 AC-9) — and what a Suggestion reaches is what the Sources page
 * shows for the same sample.
 */
export function getSamplePlanSummary(profile: SandboxProfile): SamplePlanSummary {
  const latest = profile.visibility.at(-1)
  const previous = profile.visibility.at(-2)
  const suggestions = profile.suggestions.map((s) => withReach(profile, s))
  const open = suggestions
    .filter((s) => s.task === undefined)
    .sort((a, b) => (b.buyerQuestions ?? 0) - (a.buyerQuestions ?? 0))
  const started = suggestions.filter((s) => s.task !== undefined)

  return {
    visibility: latest?.score ?? 0,
    perEngine: latest?.perEngine ?? {},
    visibilityDelta: latest && previous ? latest.score - previous.score : 0,
    suggestionCount: open.length,
    tasksLive: started.filter((s) => s.task === 'live').length,
    upNext: open.slice(0, SHOWN_ROWS),
    inProgress: started,
  }
}
